import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TopNavigationBar from './TopNavigationBar';
import {
	Button,
	Grid2,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { CloseIcon } from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import { getMsgReadStatus } from '../context/MainProvider';
import { onAddClickStat } from '../services/UserService';
import { getUserEmail, getUserId } from '../Utils/AuthUtil';

interface CustomDrawerProps {
	tabName: string;
	listItems: Array<{
		name: string;
		route: string;
		unread: boolean;
	}>;
	onClickTab: (value: string) => void;
	onClose?: () => void;
	onClickLogout?: () => void;
	isScreenInMobileMode?: boolean;
}

function CustomDrawer({
	tabName,
	listItems,
	onClickTab,
	onClose,
	onClickLogout,
	isScreenInMobileMode = false,
}: CustomDrawerProps) {
	const drawerWidth = 240;

	function getIconColor(selectedTabName: string, route: string) {
		if (
			selectedTabName === route ||
			selectedTabName.substring(0, 8) === route
		) {
			return MAIN_THEME_COLOR;
		} else {
			return '#7C7C7C';
		}
	}

	return (
		<Drawer
			sx={{
				width: isScreenInMobileMode ? '100%' : drawerWidth,
				flexShrink: 0,
				'& .MuiDrawer-paper': {
					width: isScreenInMobileMode ? '100%' : drawerWidth,
					boxSizing: 'border-box',
					borderWidth: '0px',
					marginTop: isScreenInMobileMode ? '0px' : '50px',
					height: isScreenInMobileMode ? '100%' : '95%',
				},
			}}
			variant="permanent"
			anchor="left"
			elevation={3}>
			{isScreenInMobileMode && (
				<Toolbar
					style={{
						paddingLeft: '20px',
						minHeight: '52px',
						justifyContent: 'space-between',
					}}>
					<Typography
						fontSize={'18px'}
						fontWeight={'700'}
						color={MAIN_THEME_COLOR}
						paddingLeft="5px">
						SenzToura
					</Typography>
					<Grid2
						sx={{
							flexDirection: 'column',
						}}>
						<Button
							sx={{
								marginRight: '5px',
								color: MAIN_THEME_COLOR,
								textTransform: 'none',
							}}
							onClick={onClickLogout}>
							Logout
						</Button>
						<IconButton onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Grid2>
				</Toolbar>
			)}
			<List style={{ marginTop: '15px' }}>
				{listItems.map((text, index) => (
					<ListItem
						key={index}
						disablePadding
						style={{
							backgroundColor:
								tabName === text.route ||
								tabName.substring(0, 8) === text.route
									? '#EBFAF8'
									: 'white',
							marginLeft: '10px',
							width: '90%',
							borderRadius: '6px',
						}}>
						<ListItemButton
							component={Link}
							to={text.route}
							sx={{ justifyContent: 'space-between' }}
							onClick={() => onClickTab(text.route)}>
							<Typography
								style={{
									color: getIconColor(tabName, text.route),
									fontSize: '14px',
									fontWeight: 600,
									lineHeight: '18.23px',
								}}>
								{text.name}
							</Typography>
							{text.unread && (
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: '50%',
										backgroundColor: MAIN_THEME_COLOR,
									}}
								/>
							)}
						</ListItemButton>
					</ListItem>
				))}
			</List>
		</Drawer>
	);
}

export default function SideNavigationBar() {
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { isUnreadMsgInChat, isUnreadMsgInStudio, updateMsgStatus } =
		getMsgReadStatus();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [tabName, setTabName] = useState('/');
	const [openDrawerInMobile, setOpenDrawerInMobile] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const listItems = [
		{
			name: 'Home',
			route: '/home',
			unread: false,
		},
		// {
		// 	name: 'Activity Log',
		// 	route: '/chatHistory',
		// },
		// {
		// 	name: 'Consultant Assistance',
		// 	route: '/agentRequest',
		// },
		{
			name: 'Studio',
			route: '/knowledgeUpload',
			unread: isUnreadMsgInStudio,
		},
		{
			name: 'Conversations',
			route: '/chats',
			unread: isUnreadMsgInChat,
		},
	];

	useEffect(() => {
		setTabName(location.pathname);
	}, [location.pathname]);

	function openDrawerInMobileMode() {
		setOpenDrawerInMobile(!openDrawerInMobile);
	}

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	function onClickLogout() {
		localStorage.clear();
		navigate('/');
	}

	function onAddClickStats(route: string) {
		const item = listItems.find(e => e.route === route)?.name;
		onAddClickStat(
			getUserId() ?? '',
			`${item} tab`,
			true,
			'Navigation Bar',
			getUserEmail() ? getUserEmail() : 'N/A',
			'N/A',
		);
	}

	if (isMobile) {
		return (
			<>
				<CssBaseline />
				<TopNavigationBar
					onClickLogo={openDrawerInMobileMode}
					onClickLogout={onClickLogout}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				{openDrawerInMobile && (
					<CustomDrawer
						tabName={tabName}
						listItems={listItems}
						onClickTab={value => {
							setTabName(value);
							updateMsgStatus(value);
							onAddClickStats(value);
						}}
						isScreenInMobileMode={isMobile}
						onClose={openDrawerInMobileMode}
						onClickLogout={onClickLogout}
					/>
				)}
			</>
		);
	} else {
		return (
			<Box sx={{ display: 'flex' }}>
				<CssBaseline />
				<TopNavigationBar
					onClickLogout={onClickLogout}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				<CustomDrawer
					tabName={tabName}
					listItems={listItems}
					onClickTab={value => {
						setTabName(value);
						updateMsgStatus(value);
						onAddClickStats(value);
					}}
				/>
			</Box>
		);
	}
}
